import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import StepperFormModal from '../../../../shared/components/StepperFormModal';
import CustomWarning from '../../../../shared/components/CustomWarning';
import Notification from '../../../../shared/components/Notification';
import { NotificationTypes } from '../../../../enums/notificationTypes';
import { StepperNavMode } from '../../../../enums/stepperEnums';
import { QueryKeys } from '../../../../enums/queryKeys';
import ClientManagerService, { buildClientPayload, getClientManagerErrorMessage } from '../../../../services/ClientManager/clientManager.service';
import { clientManagerServiceMessages } from '../../../../services/ClientManager/clientManager.constants';
import { useClientDetails } from '../../../../hooks/useClientDetails';
import { useClientEditRefresh } from '../../../../hooks/useClientEditRefresh';
import { usePaymentGroups } from '../../../../hooks/usePaymentGroups';
import { useAuth } from '../../../../context/AuthContext';
import { ClientFormStep } from '../../constants';
import { ClientFormMetaContext } from './clientFormMeta';
import type { ClientFormValues, CreateClientResponse, UpdateClientResponse } from '../../../../types/ClientManagementTypes';
import {
  CLIENT_FORM_DEFAULTS,
  clientFormValidation,
  detailsToClientFormValues,
  formValuesToClientPayload,
  formValuesToClientUpdatePayload,
} from './constants';
import { CLIENT_FORM_STEPS } from './steps';
import styles from './ClientFormModal.module.scss';

interface ClientFormModalProps {
  open: boolean;
  onClose: () => void;
  /** Prefills the form and switches the wizard's copy to the edit flow. */
  isEditing?: boolean;
  /** The client being edited; ignored while creating. */
  clientId?: number;
  /** Which tab the wizard opens on; Story 2 routes a row, a pill and an add-on to different ones. */
  initialStep?: ClientFormStep;
  /** Refreshes the list once a client has been created or updated. */
  onSaved?: () => void;
}

const CREATE_TITLE = 'Create New Client';
const EDIT_TITLE = 'Edit Client';
const CREATE_EXIT_MESSAGE = 'Exit client creator? Your progress will not be saved!';
const EDIT_EXIT_MESSAGE = 'Exit client editor? Your progress will not be saved!';
const MISSING_FIELDS_MESSAGE = 'Required fields are missing!';
const MODAL_WIDTH = 720;

const ClientFormModal = ({ open, onClose, isEditing = false, clientId, initialStep = ClientFormStep.CLIENT, onSaved }: ClientFormModalProps) => {
  const methods = useForm<ClientFormValues>({
    defaultValues: CLIENT_FORM_DEFAULTS,
    resolver: yupResolver(clientFormValidation) as Resolver<ClientFormValues>,
    // Errors surface on Confirm, not while typing.
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    // A failing field's step may not be mounted; focusing its null ref throws and leaves isSubmitting stuck.
    shouldFocusError: false,
  });
  const { handleSubmit, reset, formState } = methods;

  // Re-seeded on every open: the wizard never unmounts, and initialStep is read once at mount.
  const [activeStep, setActiveStep] = useState<ClientFormStep>(initialStep);
  const [isExitWarningOpen, setIsExitWarningOpen] = useState(false);
  const [isMissingFieldsWarningOpen, setIsMissingFieldsWarningOpen] = useState(false);

  const queryClient = useQueryClient();
  const { selectedClient } = useAuth();
  const { refreshQueries } = useClientEditRefresh();
  const { createClient, updateClient } = ClientManagerService();
  const { mutateAsync: createClientAsync } = useMutation({ ...createClient() });
  const { mutateAsync: updateClientAsync } = useMutation({ ...updateClient() });

  const { paymentGroups, isLoadingPaymentGroups } = usePaymentGroups();
  const { clientDetails, hasClientDetails, isClientDetailsLoadingError, clientDetailsError } = useClientDetails(clientId, open && isEditing);

  const formMeta = useMemo(
    () => ({ isEditing, currentTier: clientDetails?.subscription?.type, currentExpiry: clientDetails?.subscription?.expiresAt }),
    [clientDetails, isEditing],
  );

  // Reset on open, not close, so a closing modal doesn't visibly empty mid-animation.
  useEffect(() => {
    if (open) setActiveStep(initialStep);
  }, [open, initialStep]);

  useEffect(() => {
    if (open && !isEditing) reset(CLIENT_FORM_DEFAULTS);
  }, [open, isEditing, reset]);

  // Seeds once per edit session (tracked by clientId) so a later refetch of
  // clientDetails/paymentGroups can't reset fields the admin is still editing.
  const seededClientIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!open) {
      seededClientIdRef.current = undefined;
      return;
    }
    if (!isEditing || seededClientIdRef.current === clientId) return;
    if (!hasClientDetails || !clientDetails || isLoadingPaymentGroups) return;
    const paymentGroupName = paymentGroups.find((group) => group.id === clientDetails.client.paymentGroupId)?.name ?? '';
    reset(detailsToClientFormValues(clientDetails, paymentGroupName));
    seededClientIdRef.current = clientId;
  }, [open, isEditing, clientId, hasClientDetails, clientDetails, paymentGroups, isLoadingPaymentGroups, reset]);

  // The client may have been deleted since the menu opened; back out of an empty wizard.
  useEffect(() => {
    if (!open || !isClientDetailsLoadingError) return;
    Notification({
      message: clientManagerServiceMessages.CLIENTS.GET_DETAILS_ERROR,
      description: getClientManagerErrorMessage(clientDetailsError),
      type: NotificationTypes.ERROR,
    });
    onClose();
  }, [open, isClientDetailsLoadingError, clientDetailsError, onClose]);

  const handleCloseRequest = useCallback(() => {
    // A save is in flight; its success would land on a wizard already dismissed.
    if (formState.isSubmitting) return;
    if (formState.isDirty) {
      setIsExitWarningOpen(true);
      return;
    }
    onClose();
  }, [formState.isSubmitting, formState.isDirty, onClose]);

  const handleExitCancel = useCallback(() => setIsExitWarningOpen(false), []);

  const handleExitConfirm = useCallback(() => {
    setIsExitWarningOpen(false);
    onClose();
  }, [onClose]);

  const handleMissingFieldsAcknowledge = useCallback(() => setIsMissingFieldsWarningOpen(false), []);

  const handleSave = useCallback(
    async (values: ClientFormValues) => {
      try {
        let response: CreateClientResponse | UpdateClientResponse;

        if (isEditing) {
          // Guarded, not fallen through: an edit without an id would post a duplicate.
          if (!clientId) return;
          const payload = formValuesToClientUpdatePayload(values, formState.dirtyFields, clientDetails?.subscription ?? null);
          response = await updateClientAsync(buildClientPayload(clientId, payload));

          const refreshKeys: QueryKey[] = [[QueryKeys.GET_CLIENT_DETAILS, clientId]];
          const clientSelectorChanged =
            payload.client?.clientName !== undefined ||
            payload.payment?.paymentGroupId !== undefined ||
            payload.payment?.paymentGroupName !== undefined;

          if (clientSelectorChanged) {
            refreshKeys.push([QueryKeys.GET_CLIENTS_FOR_SELECTION]);
          }

          if (payload.subscription && selectedClient?.id === clientId) {
            refreshKeys.push([QueryKeys.GET_ASSET_MANAGER_STATUS, clientId], [QueryKeys.GET_DASHBOARD_SERVICES, clientId], [QueryKeys.GET_ALL_MAPS]);
          }

          void refreshQueries(refreshKeys);
        } else {
          response = await createClientAsync(formValuesToClientPayload(values));
        }

        Notification({
          message: response.message || (isEditing ? 'Client updated successfully' : 'Client created successfully'),
          type: NotificationTypes.SUCCESS,
        });

        if (response.paymentGroup?.created) {
          queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_PAYMENT_GROUPS] });
        }

        onSaved?.();
        onClose();
      } catch (error) {
        // The wizard stays open so the admin can correct and resubmit.
        Notification({
          message: isEditing ? clientManagerServiceMessages.CLIENTS.UPDATE_ERROR : clientManagerServiceMessages.CLIENTS.CREATE_ERROR,
          description: getClientManagerErrorMessage(error),
          type: NotificationTypes.ERROR,
        });
      }
    },
    [
      isEditing,
      clientId,
      formState.dirtyFields,
      clientDetails,
      updateClientAsync,
      createClientAsync,
      queryClient,
      selectedClient?.id,
      refreshQueries,
      onSaved,
      onClose,
    ],
  );

  // Steps are freely navigable, so Confirm is what validates the form. A step
  // with the failing field is never brought into view -- the warning at least
  // says something is wrong, since the errors alone can go unseen on a hidden tab.
  const handleConfirm = handleSubmit(handleSave, () => setIsMissingFieldsWarningOpen(true));

  return (
    <FormProvider {...methods}>
      <ClientFormMetaContext.Provider value={formMeta}>
        <StepperFormModal
          open={open}
          onClose={handleCloseRequest}
          title={isEditing ? EDIT_TITLE : CREATE_TITLE}
          steps={CLIENT_FORM_STEPS}
          width={MODAL_WIDTH}
          maskClosable
          className={styles.clientFormModal}
          step={activeStep}
          onStepChange={setActiveStep}
          navMode={StepperNavMode.FREE}
          isSubmitting={formState.isSubmitting}
          onConfirm={handleConfirm}
        />
      </ClientFormMetaContext.Provider>
      <CustomWarning
        open={isExitWarningOpen}
        onCancel={handleExitCancel}
        onConfirm={handleExitConfirm}
        message={isEditing ? EDIT_EXIT_MESSAGE : CREATE_EXIT_MESSAGE}
        cancelText="Back"
        confirmText="Exit"
        variant="dark"
        maskClosable
      />
      <CustomWarning
        open={isMissingFieldsWarningOpen}
        onCancel={handleMissingFieldsAcknowledge}
        onConfirm={handleMissingFieldsAcknowledge}
        message={MISSING_FIELDS_MESSAGE}
        confirmText="Ok"
        hideCancel
        variant="dark"
        maskClosable
      />
    </FormProvider>
  );
};

export default ClientFormModal;
